import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, LogOut, User, Clock, Activity, Shield, Bell, BellOff, Download, Volume2, VolumeX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast, Toaster } from 'sonner';
import AdminLiveChatPanel from '../components/AdminLiveChatPanel';

const getApiUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'bonbot.biz' || hostname === 'www.bonbot.biz') {
    return 'https://bonbot.biz/api';
  }
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  return `${backendUrl}/api`;
};

const API = getApiUrl();

// VAPID public key for web push (generated for this application)
const VAPID_PUBLIC_KEY = 'BBYx_gTNqOhg1zBwzY2r4zf4Z8Tu58cUXTCIEBxo203Kj5NstLiH0Q-p8-_1f6CiCcfC1B9iZdIAhTmx91-UrHk';

// Convert VAPID key to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const SupportAgentDashboard = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [agent, setAgent] = useState(null);
  const [token, setToken] = useState(null);
  
  // Login form state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  
  // PWA and Notification state
  const [isPWAInstalled, setIsPWAInstalled] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [serviceWorkerReady, setServiceWorkerReady] = useState(false);
  const [pushSubscription, setPushSubscription] = useState(null);
  
  // Audio ref for notification sound
  const notificationAudioRef = useRef(null);
  const lastChatCountRef = useRef(0);

  // Register Service Worker and handle PWA install
  useEffect(() => {
    // Add manifest link for support PWA
    const existingManifest = document.querySelector('link[rel="manifest"]');
    if (existingManifest) {
      existingManifest.href = '/support-manifest.json';
    } else {
      const manifestLink = document.createElement('link');
      manifestLink.rel = 'manifest';
      manifestLink.href = '/support-manifest.json';
      document.head.appendChild(manifestLink);
    }

    // Set theme color for PWA
    let themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (!themeColorMeta) {
      themeColorMeta = document.createElement('meta');
      themeColorMeta.name = 'theme-color';
      document.head.appendChild(themeColorMeta);
    }
    themeColorMeta.content = '#7c3aed';

    // Set page title for PWA
    document.title = 'FORTHEWISEPRO Support';

    // Register service worker with root scope for broader notification support
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/support-sw.js', { scope: '/' })
        .then((registration) => {
          console.log('Support SW registered:', registration.scope);
          setServiceWorkerReady(true);
          
          // Check for existing push subscription
          registration.pushManager.getSubscription()
            .then((subscription) => {
              if (subscription) {
                setPushSubscription(subscription);
                setNotificationsEnabled(true);
              }
            });
        })
        .catch((err) => {
          console.error('Support SW registration failed:', err);
        });
    }

    // Listen for PWA install prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      console.log('PWA install prompt ready');
    };

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsPWAInstalled(true);
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', () => {
      setIsPWAInstalled(true);
      setDeferredPrompt(null);
      toast.success('Support App installed successfully!');
    });

    // Load sound preference
    const savedSoundPref = localStorage.getItem('supportSoundEnabled');
    if (savedSoundPref !== null) {
      setSoundEnabled(savedSoundPref === 'true');
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // Install PWA
  const installPWA = async () => {
    if (!deferredPrompt) {
      toast.info('App is already installed or installation not available');
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      toast.success('Installing Support App...');
    }
    setDeferredPrompt(null);
  };

  // Request notification permission and subscribe to push
  const enableNotifications = async () => {
    try {
      // Request permission
      const permission = await Notification.requestPermission();
      
      if (permission !== 'granted') {
        toast.error('Notification permission denied');
        return;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;
      
      // Subscribe to push notifications
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });

      // Send subscription to backend
      const response = await fetch(`${API}/support/push-subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subscription: subscription.toJSON(),
          agent_token: token
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setPushSubscription(subscription);
        setNotificationsEnabled(true);
        localStorage.setItem('supportPushEnabled', 'true');
        toast.success('Push notifications enabled!');
        
        // Show test notification
        registration.showNotification('Notifications Enabled', {
          body: 'You will now receive alerts for new support requests',
          icon: '/support-icon-192.png',
          badge: '/support-icon-192.png'
        });
      } else {
        toast.error('Failed to enable notifications');
      }
    } catch (err) {
      console.error('Enable notifications error:', err);
      toast.error('Failed to enable notifications: ' + err.message);
    }
  };

  // Disable notifications
  const disableNotifications = async () => {
    try {
      if (pushSubscription) {
        await pushSubscription.unsubscribe();
        
        // Notify backend
        await fetch(`${API}/support/push-unsubscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ agent_token: token })
        });
      }
      
      setPushSubscription(null);
      setNotificationsEnabled(false);
      localStorage.removeItem('supportPushEnabled');
      toast.success('Notifications disabled');
    } catch (err) {
      console.error('Disable notifications error:', err);
    }
  };

  // Toggle sound
  const toggleSound = () => {
    const newValue = !soundEnabled;
    setSoundEnabled(newValue);
    localStorage.setItem('supportSoundEnabled', String(newValue));
    toast.success(newValue ? 'Sound enabled' : 'Sound disabled');
  };

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    if (soundEnabled && notificationAudioRef.current) {
      notificationAudioRef.current.currentTime = 0;
      notificationAudioRef.current.play().catch(err => {
        console.log('Audio play failed:', err);
      });
    }
  }, [soundEnabled]);

  useEffect(() => {
    // Check for existing session
    const storedToken = localStorage.getItem('supportAgentToken');
    if (storedToken) {
      verifyToken(storedToken);
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async (tkn) => {
    try {
      const res = await fetch(`${API}/support-agent/verify?token=${tkn}`);
      const data = await res.json();
      if (data.success) {
        setIsAuthenticated(true);
        setAgent(data.agent);
        setToken(tkn);
      } else {
        localStorage.removeItem('supportAgentToken');
      }
    } catch (err) {
      console.error('Token verification error:', err);
      localStorage.removeItem('supportAgentToken');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    
    try {
      const res = await fetch(`${API}/support-agent/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      
      const data = await res.json();
      
      if (data.success) {
        localStorage.setItem('supportAgentToken', data.token);
        setToken(data.token);
        setAgent(data.agent);
        setIsAuthenticated(true);
        toast.success('Welcome to Support Dashboard!');
      } else {
        toast.error(data.message || 'Invalid credentials');
      }
    } catch (err) {
      toast.error('Connection error. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('supportAgentToken');
    setIsAuthenticated(false);
    setAgent(null);
    setToken(null);
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <Toaster position="top-right" richColors />
        <div className="w-full max-w-md">
          {/* PWA Install Banner - Show prominently on login page */}
          {!isPWAInstalled && (
            <div className="mb-6 p-4 bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30 rounded-xl">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Download className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-bold">Install Support App</h3>
                  <p className="text-gray-300 text-sm mt-1">
                    Get push notifications for new support requests even when this page is closed.
                  </p>
                  {deferredPrompt ? (
                    <Button
                      onClick={installPWA}
                      className="mt-3 bg-purple-600 hover:bg-purple-700 text-white"
                      size="sm"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Install Now
                    </Button>
                  ) : (
                    <div className="mt-3 text-xs text-gray-400">
                      <p className="font-medium text-purple-300 mb-1">📱 To install on Android:</p>
                      <ol className="list-decimal list-inside space-y-1">
                        <li>Tap the menu button (⋮) in Chrome</li>
                        <li>Select "Add to Home screen" or "Install app"</li>
                        <li>Tap "Install" to confirm</li>
                      </ol>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {isPWAInstalled && (
            <div className="mb-6 p-3 bg-green-500/10 border border-green-500/30 rounded-xl flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <Download className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-green-400 font-medium">App Installed!</p>
                <p className="text-gray-400 text-xs">You'll receive push notifications</p>
              </div>
            </div>
          )}
          
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <MessageCircle className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white">Support Center</h1>
            <p className="text-gray-400 mt-2">Sign in to access live chat support</p>
          </div>
          
          <form onSubmit={handleLogin} className="bg-slate-800/50 border border-white/10 rounded-xl p-6 space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full bg-slate-900 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-purple-500 focus:outline-none"
                placeholder="Enter username"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm text-gray-400 mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-slate-900 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-purple-500 focus:outline-none"
                placeholder="Enter password"
                required
              />
            </div>
            
            <Button
              type="submit"
              disabled={loginLoading}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 py-6"
            >
              {loginLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
          
          <p className="text-center text-gray-500 text-xs mt-4">
            FORTHEWISEPRO LLC - Support Portal
          </p>
        </div>
      </div>
    );
  }

  // Support Agent Dashboard
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4 md:p-8">
      <Toaster position="top-right" richColors />
      
      {/* Hidden audio element for notification sound */}
      <audio ref={notificationAudioRef} preload="auto">
        <source src="/notification-sound.wav" type="audio/wav" />
      </audio>
      
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
              <MessageCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Support Dashboard</h1>
              <div className="flex items-center gap-2">
                <p className="text-gray-400">Welcome, {agent?.name || agent?.username}</p>
                <Badge className="bg-blue-500">Support Agent</Badge>
              </div>
            </div>
          </div>
          
          {/* Control Buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* PWA Install Button */}
            {!isPWAInstalled && deferredPrompt && (
              <Button
                onClick={installPWA}
                className="bg-green-600 hover:bg-green-700 text-white"
                size="sm"
              >
                <Download className="w-4 h-4 mr-2" />
                Install App
              </Button>
            )}
            
            {isPWAInstalled && (
              <Badge className="bg-green-500/20 text-green-400">
                <Download className="w-3 h-3 mr-1" />
                App Installed
              </Badge>
            )}
            
            {/* Notification Toggle */}
            {notificationsEnabled ? (
              <Button
                onClick={disableNotifications}
                variant="outline"
                size="sm"
                className="border-green-500 text-green-400 hover:bg-green-500/10"
              >
                <Bell className="w-4 h-4 mr-2" />
                Notifications On
              </Button>
            ) : (
              <Button
                onClick={enableNotifications}
                variant="outline"
                size="sm"
                className="border-gray-500 text-gray-400 hover:bg-gray-500/10"
              >
                <BellOff className="w-4 h-4 mr-2" />
                Enable Notifications
              </Button>
            )}
            
            {/* Sound Toggle */}
            <Button
              onClick={toggleSound}
              variant="ghost"
              size="sm"
              className={soundEnabled ? 'text-purple-400' : 'text-gray-500'}
            >
              {soundEnabled ? (
                <Volume2 className="w-4 h-4" />
              ) : (
                <VolumeX className="w-4 h-4" />
              )}
            </Button>
            
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
        
        {/* PWA Install Banner - Show on mobile if not installed */}
        {!isPWAInstalled && !deferredPrompt && (
          <div className="mt-4 p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
            <div className="flex items-center gap-3">
              <Download className="w-6 h-6 text-purple-400" />
              <div>
                <p className="text-white font-medium">Install Support App</p>
                <p className="text-gray-400 text-sm">
                  Add this page to your home screen for push notifications and quick access.
                  On Android: Tap the menu (⋮) → "Add to Home screen"
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Live Chat Panel - Full Width for Support Agents */}
      <div className="max-w-6xl mx-auto">
        <AdminLiveChatPanel 
          token={token} 
          API={API} 
          isCustomerSupport={true}
          onNewChat={playNotificationSound}
        />
      </div>
      
      {/* Simple Footer */}
      <div className="max-w-6xl mx-auto mt-8 text-center text-gray-500 text-sm">
        FORTHEWISEPRO LLC - Customer Support Portal
      </div>
    </div>
  );
};

export default SupportAgentDashboard;
